/* eslint-disable react/require-default-props */
import React, { useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer, Formats, View } from 'react-big-calendar';
import { Box, Skeleton, Typography, useMediaQuery } from '@mui/material';
import {
  format,
  parseISO,
  startOfWeek,
  getDay,
  startOfMonth,
  endOfMonth,
  endOfWeek,
  startOfDay,
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Trips } from '@prisma/client';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Appointment, LoadedUser } from '../../../interfaces';
import useGetBatchTimeBlocks from '../../../hooks/employees/useGetBatchTimeBlocks';
import useGetBatchTrips from '../../../hooks/employees/useGetBatchTrips';
import useUser from '../../../hooks/global/useUser';
import { CalendarEvent } from '../../../types/calendar';
import useSizeOptions from '../../../hooks/tattoos/useSizeOptions';
import {
  getCalendarEvents,
  getEarliestAvailabilities,
  slotPropGetter,
} from '../../../helpers/calendar';
import useOnschedResourceBatch from '../../../hooks/employees/useOnschedResourceBatch';
import './styles.css';
import { ResourceBlock } from '../../../types/employees';
import TripSideBarModal from '../../trips/TripSideBarModal';
import TimeBlockSideBarModal from '../../timeBlocks/TimeBlockSideBarModal';
import { cx } from '../../../helpers/global';
import { getEmployeesResourcesList } from '../../../services/business';
import { convertIndexToShortDay } from '../../../utils/global/helper';
import { withSelectedArtists } from './ResourceTitle';

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse: parseISO,
  startOfWeek,
  getDay,
  locales,
});

interface CalendarProps {
  artistOptions: string[];
  appointments?: Appointment[];
  selectedArtists: string[];
  setSelectedArtists: React.Dispatch<React.SetStateAction<string[]>>;
  view: View;
  setView: React.Dispatch<React.SetStateAction<View>>;
  date: Date;
  setDate: (newDate: Date) => void;
  openAppointment: (appointment: Appointment) => void;
  refreshModal: (appointmentId: string) => void;
}

interface SelectedTimeBlock extends ResourceBlock {
  employeeId: string;
}

interface EventComponentProps {
  event: CalendarEvent;
}

function MonthEvent({ event }: EventComponentProps) {
  if (!event.start) return null;
  return (
    <div
      className={cx(
        `${event.type}-event`,
        event.type === 'appointment' && event.canceled && 'canceled',
      )}
      style={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        padding: `${
          event.type === 'appointment' && !event.canceled ? '3px' : '2px'
        } 2px`,
        overflow: 'hidden',
      }}>
      <Typography
        variant="caption"
        style={{ marginRight: 4 }}
        color={event.textColor || 'white'}>
        {format(event.start, 'h:mma')} •
      </Typography>
      <Typography
        variant="h5"
        color={event.textColor || 'white'}
        style={{ fontSize: 12 }}>
        {event.title}
      </Typography>
    </div>
  );
}

function WeekEvent({ event }: EventComponentProps) {
  return (
    <div
      className={cx(
        `${event.type}-event`,
        event.type === 'appointment' && event.canceled && 'canceled',
      )}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        overflow: 'hidden',
        marginTop: 5,
      }}>
      <Typography
        variant="h5"
        color={event.textColor || 'white'}
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '100%',
        }}>
        {event.title}
      </Typography>
      {event.detailsComponent}
    </div>
  );
}

export default function WeekView({
  artistOptions,
  appointments,
  selectedArtists,
  setSelectedArtists,
  view,
  date,
  setDate,
  setView,
  openAppointment,
  refreshModal,
}: CalendarProps) {
  const isDesktop = useMediaQuery('(min-width:900px)');

  const { user: loadedUser } = useUser<LoadedUser>();
  const { data: timeBlocks } = useGetBatchTimeBlocks({
    employeeIds: selectedArtists ?? [],
    dataStartDate: startOfWeek(startOfMonth(date)),
    dataEndDate: endOfWeek(endOfMonth(date)),
  });
  const { data: trips } = useGetBatchTrips({
    employeeIds: selectedArtists ?? [],
  });
  const {
    data: employeesOnschedResources,
    isLoading: isLoadingResources,
    isError: isResourcesError,
  } = useOnschedResourceBatch({
    employeeIds: artistOptions,
  });

  const { sizeOptions } = useSizeOptions();

  const [selectedTrip, setSelectedTrip] = useState<Trips | null>(null);
  const [selectedTimeBlock, setSelectedTimeBlock] =
    useState<SelectedTimeBlock | null>(null);

  const resourceMap = React.useMemo(() => {
    let list = getEmployeesResourcesList(loadedUser);
    if (selectedArtists.length !== 1 || selectedArtists[0] !== 'Initialize') {
      list = list.filter(resource =>
        selectedArtists.includes(resource.resourceId),
      );
    }
    return list;
  }, [loadedUser, selectedArtists]);

  const scrollToTime = React.useMemo(() => {
    if (!employeesOnschedResources || view === 'month') return undefined;

    const earliestAvailabilities = getEarliestAvailabilities(
      employeesOnschedResources,
      selectedArtists,
    );

    if (view === 'day') {
      const dayIndex = getDay(date);
      const day = convertIndexToShortDay(dayIndex);
      const earliestDate = earliestAvailabilities[day];
      if (!earliestDate) return startOfDay(date);
      return earliestDate;
    }

    if (view === 'week') {
      const minimum = Object.values(earliestAvailabilities).reduce<Date | null>(
        (min, currentDate) =>
          !min || currentDate.getTime() < min.getTime() ? currentDate : min,
        null,
      );
      return minimum ?? startOfDay(date);
    }

    return undefined;
  }, [date, employeesOnschedResources, selectedArtists, view]);

  const events = getCalendarEvents({
    appointments: appointments ?? [],
    trips,
    timeBlocks,
    resourceMap,
    sizeOptions,
    employeesToBusiness: loadedUser.business.employees,
  });

  const handleClick = (event: CalendarEvent) => {
    if (event.type === 'appointment') {
      const appointment = appointments?.find(
        apt => apt.appointmentId === event.appointmentId,
      );
      if (!appointment) return;
      openAppointment(appointment);
      refreshModal(appointment.appointmentId); // always get fresh data
    } else if (event.type === 'trip') {
      const trip = trips?.[event.resourceId]?.find(
        t => t.tripId === event.tripId,
      );
      if (!trip) return;
      setSelectedTrip(trip);
    } else if (event.type === 'time-block') {
      const timeBlock = timeBlocks?.[event.resourceId]?.find(
        tb => tb.id === event.timeBlockId,
      );
      if (!timeBlock) return;
      setSelectedTimeBlock({ ...timeBlock, employeeId: event.resourceId });
    }
  };

  const eventStyleGetter = (event: any) => {
    const backgroundColor = event.color;
    return {
      style: {
        backgroundColor,
      },
    };
  };

  const formats: Formats = {
    weekdayFormat: (d, culture, l) => l?.format(d, 'eee', culture) || '',

    dateFormat: (d, culture, l) => l?.format(d, 'd', culture) || '',

    dayFormat: (d, culture, l) => l?.format(d, 'eee M/d', culture) || '',

    timeGutterFormat: (d, culture, l) => l?.format(d, 'h aaa', culture) || '',
  };

  const updatedSelectedTimeBlock = useMemo(() => {
    if (!selectedTimeBlock) return null;
    return Object.entries(timeBlocks).reduce<
      (ResourceBlock & { employeeId: string }) | null
    >((acc, [employeeId, blocks]) => {
      const block = blocks.find(tb => tb.id === selectedTimeBlock.id);
      if (block) {
        return { ...block, employeeId };
      }
      return acc;
    }, null);
  }, [selectedTimeBlock, timeBlocks]);

  // Fallback UI for when appointments is undefined
  if (!appointments || (isLoadingResources && !isResourcesError)) {
    return (
      <div style={{ height: '700px', margin: '40px 0' }}>
        <Skeleton
          variant="rectangular"
          width="100%"
          height={80}
          style={{ marginBottom: 10 }}
        />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={80}
          style={{ marginBottom: 10 }}
        />
        <Skeleton
          variant="rectangular"
          width="100%"
          height={80}
          style={{ marginBottom: 10 }}
        />
      </div>
    );
  }

  return (
    <>
      <Box
        sx={{
          height: {
            xs: 'calc(100svh - 355px)',
            md: 620,
          },
        }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{
            height: isDesktop ? 620 : 'calc(100svh - 355px)',
          }}
          step={30}
          date={date}
          view={view}
          onNavigate={newDate => setDate(newDate)}
          onView={newView => setView(newView)}
          eventPropGetter={eventStyleGetter}
          resourceIdAccessor="resourceId"
          resourceTitleAccessor="resourceTitle"
          resources={
            view === 'day' || (view === 'week' && resourceMap?.length === 1)
              ? resourceMap
              : undefined
          }
          toolbar={false}
          formats={formats}
          onSelectEvent={event => handleClick(event)}
          components={{
            month: { event: MonthEvent },
            week: { event: WeekEvent },
            day: { event: WeekEvent },
            resourceHeader: withSelectedArtists(setSelectedArtists),
          }}
          allDayMaxRows={1}
          showMultiDayTimes
          slotPropGetter={(d, resourceId) =>
            slotPropGetter(d, resourceId, employeesOnschedResources, timeBlocks)
          }
          dayPropGetter={() => ({
            style: {
              backgroundColor: 'white',
            },
          })}
          scrollToTime={scrollToTime}
        />
      </Box>
      <TripSideBarModal
        trip={selectedTrip}
        onClose={() => setSelectedTrip(null)}
      />
      <TimeBlockSideBarModal
        selectedArtists={selectedArtists}
        timeBlock={updatedSelectedTimeBlock}
        onClose={() => setSelectedTimeBlock(null)}
      />
    </>
  );
}
